import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { Box, Group, Loader, Center, Container, Grid, Alert } from '@mantine/core'
import { AppNumberInput, AppSelect, AppTextarea } from '../../app/common/ui'
import { AppButton, AppCard } from '@gunslop/ui'
import { AppText, AppStack } from '../../app/common/ui'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import agent from '../../app/api/agent'
import { useStore } from '../../app/stores/store'
import { ReloaderRecipeFormValues } from '../../app/models/reloaderRecipe'
import { CartridgeSpecDto } from '../../app/models/ammo'
import MyTextInput from '../../app/common/form/MyTextInput'
import LabelWithHelp from '../help/LabelWithHelp'
import { RecipeHelpFieldKey } from '../help/recipeHelpContent'
import {
  RECIPE_SUBMIT_RETURN_PATH,
  buildRecipeHelpUrl,
  consumeRecipeSubmitFormState,
  saveRecipeSubmitFormState
} from '../help/recipeSubmitFormState'
import { applyAmmoRecipeFill, isAmmoRecipeFillResultValid } from './applyAmmoRecipeFill'
import { getAmmoAiFillErrorMessage, AI_FILL_FAILED_MESSAGE } from './ammoAiFillErrors'
import AiFillStatusAnnouncer from './AiFillStatusAnnouncer'

const optionalPositiveNumber = () =>
  Yup.number()
    .transform((value, originalValue) => {
      if (originalValue === '' || originalValue === null || originalValue === undefined) {
        return undefined
      }
      return value
    })
    .optional()
    .test('positive', 'Must be positive', (v) => v === undefined || v > 0)

const validationSchema = Yup.object({
  cartridgeSpecId: Yup.string().required('Cartridge is required'),
  powderBrand: Yup.string().required('Powder brand is required'),
  powderType: Yup.string().required('Powder type is required'),
  chargeWeightGrMin: Yup.number().required('Min charge weight is required').positive('Must be positive'),
  chargeWeightGrMax: Yup.number().required('Max charge weight is required').positive('Must be positive'),
  bulletBrand: Yup.string().optional(),
  bulletWeightGr: optionalPositiveNumber(),
  bulletType: Yup.string().optional(),
  oalIn: optionalPositiveNumber(),
  primerBrand: Yup.string().optional(),
  primerType: Yup.string().optional(),
  brassBrand: Yup.string().optional(),
  coalIn: optionalPositiveNumber(),
  seatingDepthIn: optionalPositiveNumber(),
  estimatedVelocityFps: optionalPositiveNumber(),
  chronoData: Yup.string().optional(),
  intendedUse: Yup.string().optional(),
  notes: Yup.string().optional()
})

const BULLET_TYPES = [
  { value: 'FMJ', label: 'FMJ (Full Metal Jacket)' },
  { value: 'JHP', label: 'JHP (Jacketed Hollow Point)' },
  { value: 'SP', label: 'SP (Soft Point)' },
  { value: 'HPBT', label: 'HPBT (Hollow Point Boat Tail)' },
  { value: 'other', label: 'Other' }
]

const EMBER_ACCENT = 'var(--gs-accent, #e85d04)'

const titleSx = {
  fontFamily: 'Oswald, sans-serif',
  fontSize: '32px',
  fontWeight: 700,
  color: 'var(--gs-text)',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.02em',
  borderBottom: `3px solid ${EMBER_ACCENT}`,
  display: 'inline-block',
  paddingBottom: '4px',
  marginBottom: '8px'
}

export function defaultRecipeFormValues (): ReloaderRecipeFormValues {
  return {
    cartridgeSpecId: '',
    powderBrand: '',
    powderType: '',
    chargeWeightGrMin: undefined as unknown as number,
    chargeWeightGrMax: undefined as unknown as number,
    primerBrand: '',
    primerType: '',
    brassBrand: '',
    bulletBrand: '',
    bulletWeightGr: undefined as unknown as number,
    bulletType: '',
    oalIn: undefined as unknown as number,
    coalIn: undefined,
    seatingDepthIn: undefined,
    estimatedVelocityFps: undefined,
    chronoData: '',
    intendedUse: 'target',
    notes: ''
  }
}

function coerceRecipePayload (values: ReloaderRecipeFormValues): ReloaderRecipeFormValues {
  return {
    ...values,
    bulletWeightGr: values.bulletWeightGr ?? 0,
    oalIn: values.oalIn ?? 0,
    coalIn: values.coalIn,
    seatingDepthIn: values.seatingDepthIn,
    estimatedVelocityFps: values.estimatedVelocityFps
  }
}

function RecipePageHeader ({ subtitle }: { subtitle: string }) {
  return (
    <Box>
      <AppText sx={titleSx}>Submit Recipe</AppText>
      <AppText sx={{ color: 'var(--gs-muted)', fontSize: '14px' }}>{subtitle}</AppText>
    </Box>
  )
}

export default function SubmitRecipePage () {
  const history = useHistory()
  const { userStore } = useStore()
  const [cartridges, setCartridges] = useState<CartridgeSpecDto[]>([])
  const [loadingCartridges, setLoadingCartridges] = useState(true)
  const [initialForm, setInitialForm] = useState<ReloaderRecipeFormValues>(defaultRecipeFormValues)
  const [pasteText, setPasteText] = useState('')
  const [extractLoading, setExtractLoading] = useState(false)
  const [aiStatusMessage, setAiStatusMessage] = useState<string | null>(null)
  const formValuesRef = useRef<ReloaderRecipeFormValues>(defaultRecipeFormValues())
  const setFormValuesRef = useRef<((values: ReloaderRecipeFormValues) => void) | null>(null)

  useEffect(() => {
    const restored = consumeRecipeSubmitFormState()
    if (restored) {
      setInitialForm(restored.form)
    }
  }, [])

  useEffect(() => {
    const loadCartridges = async () => {
      try {
        const result = await agent.CartridgeSpecs.list({ pageSize: 100 })
        setCartridges(result.items)
      } catch {
        toast.error('Failed to load cartridges')
      } finally {
        setLoadingCartridges(false)
      }
    }
    loadCartridges()
  }, [])

  const persistAndOpenHelp = useCallback(
    (field: RecipeHelpFieldKey, form: ReloaderRecipeFormValues) => {
      const snapshot = { form }
      saveRecipeSubmitFormState(snapshot)
      history.push(buildRecipeHelpUrl(field, RECIPE_SUBMIT_RETURN_PATH, snapshot))
    },
    [history]
  )

  const canSubmit = userStore.user && userStore.user.ammoScore >= 50

  const handleRecipeExtract = async () => {
    const pastedText = pasteText.trim()
    if (!pastedText) return
    setExtractLoading(true)
    try {
      const result = await agent.AiAssist.ammoRecipeFill({ pastedText })
      if (!isAmmoRecipeFillResultValid(result)) {
        toast.error(AI_FILL_FAILED_MESSAGE)
        setAiStatusMessage(AI_FILL_FAILED_MESSAGE)
        return
      }
      const catalogIds = new Set(cartridges.map((c) => c.id))
      const next = applyAmmoRecipeFill(result, catalogIds, formValuesRef.current)
      setFormValuesRef.current?.(next)
      setInitialForm(next)
      const successMsg = 'Suggestions applied — verify against manuals.'
      toast.success(successMsg)
      setAiStatusMessage(successMsg)
    } catch (error) {
      const msg = getAmmoAiFillErrorMessage(error)
      toast.error(msg)
      setAiStatusMessage(msg)
    } finally {
      setExtractLoading(false)
    }
  }

  const handleSubmit = async (values: ReloaderRecipeFormValues) => {
    try {
      await agent.ReloaderRecipes.create(coerceRecipePayload(values))
      toast.success('Recipe submitted! It will appear after a brief review.')
      history.push('/ammo/reloading')
    } catch {
      toast.error('Failed to submit recipe')
    }
  }

  const shell = (children: React.ReactNode) => (
    <Box sx={{ background: 'var(--gs-bg)', minHeight: '100vh' }}>
      <Box sx={{ borderBottom: `3px solid ${EMBER_ACCENT}`, marginBottom: '24px' }}>
        <Container size="md" sx={{ paddingTop: '20px', paddingBottom: '8px' }}>
          <AppButton
            variant="subtle"
            onClick={() => history.push('/ammo/reloading')}
            sx={{ width: 'fit-content', paddingLeft: 0, marginBottom: 8 }}
          >
            ← Back to Reloader Forum
          </AppButton>
        </Container>
      </Box>
      <Container size="md" sx={{ paddingBottom: '32px', position: 'relative' }}>
        <AiFillStatusAnnouncer message={aiStatusMessage} />
        <AppStack spacing="lg">{children}</AppStack>
      </Container>
    </Box>
  )

  if (!canSubmit) {
    return shell(
      <>
        <RecipePageHeader subtitle="From manuals and field testing. Experts only." />
        <AppCard sx={{ background: 'var(--gs-surface)' }}>
          <AppText size="lg" weight={600} mb="md" sx={{ color: EMBER_ACCENT }}>
            Recipe Submissions Require Contributor Tier
          </AppText>
          <AppText size="sm" sx={{ color: 'var(--gs-muted)' }} mb="md">
            Recipe submissions require at least 50 AmmoScore to ensure community submissions meet safety standards.
          </AppText>
          <AppText size="sm" mb="md">
            Your current score: <strong>{userStore.user?.ammoScore ?? 0}</strong>
          </AppText>
          <AppText size="sm" sx={{ color: 'var(--gs-muted)' }}>
            Keep contributing through posts, reviews, and votes to unlock this feature.
          </AppText>
        </AppCard>
      </>
    )
  }

  if (loadingCartridges) {
    return (
      <Box sx={{ background: 'var(--gs-bg)', minHeight: '100vh' }}>
        <Center py="xl">
          <Loader />
        </Center>
      </Box>
    )
  }

  return shell(
    <>
      <RecipePageHeader subtitle="From manuals and field testing. Experts only." />

      <AppCard sx={{ background: 'var(--gs-surface)' }}>
        <AppText weight={600} size="sm" mb="xs">
          Paste from manual
        </AppText>
        <AppText size="xs" sx={{ color: 'var(--gs-muted)' }} mb="sm">
          Optional: paste load data to extract fields. Verify every value against published manuals before submitting.
        </AppText>
        <AppTextarea
          placeholder="Paste load data from manual"
          value={pasteText}
          onChange={(e) => setPasteText(e.currentTarget.value)}
          rows={4}
          mb="sm"
          data-testid="recipe-paste-textarea"
        />
        <Group position="right" mb="md">
          <AppButton
            onClick={handleRecipeExtract}
            loading={extractLoading}
            disabled={!pasteText.trim()}
            data-testid="recipe-extract-button"
          >
            Extract
          </AppButton>
        </Group>
      </AppCard>

      <AppCard sx={{ background: 'var(--gs-surface)' }}>
        <Formik
          initialValues={initialForm}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ handleSubmit, isSubmitting, isValid, values, setFieldValue, setValues, errors, touched }) => {
            formValuesRef.current = values
            setFormValuesRef.current = setValues
            return (
            <Form onSubmit={handleSubmit}>
              <Alert
                color="yellow"
                title="Safety note"
                mb="md"
                data-testid="recipe-safety-disclaimer"
                role="status"
              >
                Verify all loads against published reloading manuals (Hodgdon, Nosler, etc). This is a community log,
                not load data.
              </Alert>

              <AppSelect
                label="Cartridge"
                placeholder="Select cartridge"
                searchable
                required
                value={values.cartridgeSpecId}
                onChange={(v) => setFieldValue('cartridgeSpecId', v || '')}
                data={cartridges.map((c) => ({ value: c.id, label: c.name }))}
                error={touched.cartridgeSpecId && errors.cartridgeSpecId}
                mb="xs"
              />
              <AppText size="sm" sx={{ color: 'var(--gs-muted)' }} mb="lg">
                All fields optional except cartridge and powder. Fill in what you have verified.
              </AppText>

              <AppText weight={600} size="sm" mb="sm">
                Powder Specifications
              </AppText>
              <Grid gutter="md" mb="md">
                <Grid.Col span={12} md={6}>
                  <Box>
                    <LabelWithHelp
                      label="Powder brand"
                      required
                      onHelpClick={() => persistAndOpenHelp('powderBrand', values)}
                    />
                    <MyTextInput
                      name="powderBrand"
                      aria-label="Powder brand"
                      placeholder="Hodgdon"
                      data-testid="recipe-submit-powder-brand"
                    />
                  </Box>
                </Grid.Col>
                <Grid.Col span={12} md={6}>
                  <Box>
                    <LabelWithHelp
                      label="Powder type"
                      required
                      onHelpClick={() => persistAndOpenHelp('powderType', values)}
                    />
                    <MyTextInput
                      name="powderType"
                      aria-label="Powder type"
                      placeholder="Varget"
                      data-testid="recipe-submit-powder-type"
                    />
                  </Box>
                </Grid.Col>
                <Grid.Col span={12}>
                  <LabelWithHelp
                    label="Charge weight (gr)"
                    required
                    onHelpClick={() => persistAndOpenHelp('chargeWeightGr', values)}
                  />
                  <Group grow>
                    <AppNumberInput
                      aria-label="Min charge weight gr"
                      placeholder="25.5"
                      value={values.chargeWeightGrMin}
                      onChange={(v) => setFieldValue('chargeWeightGrMin', v ?? undefined)}
                      error={touched.chargeWeightGrMin && errors.chargeWeightGrMin}
                    />
                    <AppNumberInput
                      aria-label="Max charge weight gr"
                      placeholder="26.5"
                      value={values.chargeWeightGrMax}
                      onChange={(v) => setFieldValue('chargeWeightGrMax', v ?? undefined)}
                      error={touched.chargeWeightGrMax && errors.chargeWeightGrMax}
                    />
                  </Group>
                </Grid.Col>
              </Grid>

              <AppText weight={600} size="sm" mt="md" mb="sm">
                Primer Specifications
              </AppText>
              <Grid gutter="md" mb="md">
                <Grid.Col span={12} md={6}>
                  <Box>
                    <LabelWithHelp
                      label="Primer brand"
                      onHelpClick={() => persistAndOpenHelp('primerBrand', values)}
                    />
                    <MyTextInput name="primerBrand" aria-label="Primer brand" placeholder="CCI" />
                  </Box>
                </Grid.Col>
                <Grid.Col span={12} md={6}>
                  <Box>
                    <LabelWithHelp
                      label="Primer type"
                      onHelpClick={() => persistAndOpenHelp('primerType', values)}
                    />
                    <MyTextInput name="primerType" aria-label="Primer type" placeholder="400 (Small Rifle)" />
                  </Box>
                </Grid.Col>
              </Grid>

              <AppText weight={600} size="sm" mt="md" mb="sm">
                Brass & Bullet
              </AppText>
              <Grid gutter="md" mb="md">
                <Grid.Col span={12} md={6}>
                  <Box>
                    <LabelWithHelp
                      label="Brass brand"
                      onHelpClick={() => persistAndOpenHelp('brassBrand', values)}
                    />
                    <MyTextInput name="brassBrand" aria-label="Brass brand" placeholder="Federal" />
                  </Box>
                </Grid.Col>
                <Grid.Col span={12} md={6}>
                  <Box>
                    <LabelWithHelp
                      label="Bullet brand"
                      onHelpClick={() => persistAndOpenHelp('bulletBrand', values)}
                    />
                    <MyTextInput name="bulletBrand" aria-label="Bullet brand" placeholder="Speer" />
                  </Box>
                </Grid.Col>
                <Grid.Col span={12} md={6}>
                  <Box>
                    <LabelWithHelp
                      label="Bullet weight (gr)"
                      onHelpClick={() => persistAndOpenHelp('bulletWeightGr', values)}
                    />
                    <AppNumberInput
                      aria-label="Bullet weight gr"
                      placeholder="55"
                      value={values.bulletWeightGr}
                      onChange={(v) => setFieldValue('bulletWeightGr', v ?? undefined)}
                      error={touched.bulletWeightGr && errors.bulletWeightGr}
                    />
                  </Box>
                </Grid.Col>
                <Grid.Col span={12} md={6}>
                  <Box>
                    <LabelWithHelp
                      label="Bullet type"
                      onHelpClick={() => persistAndOpenHelp('bulletType', values)}
                    />
                    <AppSelect
                      aria-label="Bullet type"
                      placeholder="FMJ"
                      value={values.bulletType || null}
                      onChange={(v) => setFieldValue('bulletType', v || '')}
                      data={BULLET_TYPES}
                      error={touched.bulletType && errors.bulletType}
                    />
                  </Box>
                </Grid.Col>
              </Grid>

              <AppText weight={600} size="sm" mt="md" mb="sm">
                Dimensions
              </AppText>
              <Grid gutter="md" mb="md">
                <Grid.Col span={12} md={4}>
                  <Box>
                    <LabelWithHelp label="OAL (inches)" onHelpClick={() => persistAndOpenHelp('oalIn', values)} />
                    <AppNumberInput
                      aria-label="OAL inches"
                      placeholder="2.260"
                      value={values.oalIn}
                      onChange={(v) => setFieldValue('oalIn', v ?? undefined)}
                      error={touched.oalIn && errors.oalIn}
                      precision={3}
                      step={0.001}
                    />
                  </Box>
                </Grid.Col>
                <Grid.Col span={12} md={4}>
                  <Box>
                    <LabelWithHelp label="COAL (inches)" onHelpClick={() => persistAndOpenHelp('coalIn', values)} />
                    <AppNumberInput
                      aria-label="COAL inches"
                      placeholder="2.500"
                      value={values.coalIn}
                      onChange={(v) => setFieldValue('coalIn', v ?? undefined)}
                      precision={3}
                      step={0.001}
                    />
                  </Box>
                </Grid.Col>
                <Grid.Col span={12} md={4}>
                  <Box>
                    <LabelWithHelp
                      label="Seating depth (inches)"
                      onHelpClick={() => persistAndOpenHelp('seatingDepthIn', values)}
                    />
                    <AppNumberInput
                      aria-label="Seating depth inches"
                      placeholder="0.125"
                      value={values.seatingDepthIn}
                      onChange={(v) => setFieldValue('seatingDepthIn', v ?? undefined)}
                      precision={3}
                      step={0.001}
                    />
                  </Box>
                </Grid.Col>
              </Grid>

              <AppText weight={600} size="sm" mt="md" mb="sm">
                Performance Data
              </AppText>
              <Grid gutter="md" mb="md">
                <Grid.Col span={12} md={6}>
                  <Box>
                    <LabelWithHelp
                      label="Estimated velocity (fps)"
                      onHelpClick={() => persistAndOpenHelp('estimatedVelocityFps', values)}
                    />
                    <AppNumberInput
                      aria-label="Estimated velocity fps"
                      placeholder="3050"
                      value={values.estimatedVelocityFps}
                      onChange={(v) => setFieldValue('estimatedVelocityFps', v ?? undefined)}
                    />
                  </Box>
                </Grid.Col>
                <Grid.Col span={12}>
                  <Box>
                    <LabelWithHelp
                      label="Chrono data"
                      onHelpClick={() => persistAndOpenHelp('chronoData', values)}
                    />
                    <AppTextarea
                      aria-label="Chrono data"
                      placeholder="Paste actual velocity readings here..."
                      value={values.chronoData || ''}
                      onChange={(e) => setFieldValue('chronoData', e.currentTarget.value)}
                      rows={4}
                    />
                  </Box>
                </Grid.Col>
              </Grid>

              <AppText weight={600} size="sm" mt="md" mb="sm">
                Additional Info
              </AppText>
              <Grid gutter="md" mb="lg">
                <Grid.Col span={12} md={6}>
                  <AppSelect
                    label="Intended use"
                    placeholder="Select use case"
                    value={values.intendedUse}
                    onChange={(v) => setFieldValue('intendedUse', v || 'target')}
                    data={[
                      { value: 'target', label: 'Target' },
                      { value: 'hunting', label: 'Hunting' },
                      { value: 'defense', label: 'Defense' },
                      { value: 'plinking', label: 'Plinking' }
                    ]}
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <AppTextarea
                    label="Notes"
                    placeholder="Any special notes or observations?"
                    value={values.notes || ''}
                    onChange={(e) => setFieldValue('notes', e.currentTarget.value)}
                    rows={3}
                  />
                </Grid.Col>
              </Grid>

              <Group position="right">
                <AppButton variant="light" onClick={() => history.push('/ammo/reloading')}>
                  Cancel
                </AppButton>
                <AppButton type="submit" disabled={isSubmitting || !isValid} loading={isSubmitting}>
                  Submit Recipe
                </AppButton>
              </Group>
            </Form>
            )
          }}
        </Formik>
      </AppCard>
    </>
  )
}
